import { BiddingStrategy } from '../bidding-strategies/bidding_strategies'
import { resources, common, enums } from '../gads'
import { UUID } from '../utils/global'

export enum PerformanceMode {
    UNSPECIFIED = 0,
    CPA = 1,
    ROAS = 2,
}

export interface AccountTargets {
    performanceMode: PerformanceMode
    desiredCpa: number | null
    desiredRoas: number | null
}

export enum CampaignGroupType {
    BRAND = 0, // The default brand campaign group, to which all new brand campaigns are added
    SEARCH = 1, // The default search campaign group, to which all new campaigns are added
    DISPLAY = 2,
    SHOPPING = 3,
    VIDEO = 4,
    OTHER = 5, // All unsupported Advertising Channel Types or legacy campaign groups
    HOTEL = 6,
    MULTI_CHANNEL = 7,
    LOCAL = 8,
    SMART = 9,
    PERFORMANCE_MAX = 10,
    LOCAL_SERVICES = 11,
    DEMAND_GEN = 12,
    TRAVEL = 13,
    APP_PROMOTION = 15,
    AWARENESS = 16,
    EXCLUDED = 14, // Campaigns in this group are excluded from improvements
    TRAFFIC = 17,
    ENGAGEMENT = 18,
    LEADS = 19,
    SALES = 20,
    CONVERSIONS = 21,
    MOBILE_APP_ENGAGEMENT = 22,
}

export enum TargetMode {
    AUTO = 0, // Using the average as a target
    ACCOUNT_TARGET = 1, // Using the account target
    CUSTOM = 2, // Using a custom target
}

export const PrimaryPerformanceGoal = {
    CONVERSIONS: 'CONVERSIONS',
    CONVERSION_VALUE: 'CONVERSION_VALUE',
} as const

export type PrimaryPerformanceGoal = keyof typeof PrimaryPerformanceGoal

export const SecondaryPerformanceGoal = {
    IMPROVE_CTR: 'IMPROVE_CTR',
    IMPROVE_SIS: 'IMPROVE_SIS',
    IMPROVE_AVG_QS: 'IMPROVE_AVG_QS',
    REDUCE_CPC: 'REDUCE_CPC',
} as const

export type SecondaryPerformanceGoal = keyof typeof SecondaryPerformanceGoal

export const PerformanceGoalCycle = {
    DAY: 'DAY',
    WEEK: 'WEEK',
    MONTH: 'MONTH',
    QUARTER: 'QUARTER',
    YEAR: 'YEAR',
} as const

export type PerformanceGoalCycle = keyof typeof PerformanceGoalCycle

export const PerformanceGoalCycleStartDay = {
    MONDAY: 'MONDAY',
    TUESDAY: 'TUESDAY',
    WEDNESDAY: 'WEDNESDAY',
    THURSDAY: 'THURSDAY',
    FRIDAY: 'FRIDAY',
    SATURDAY: 'SATURDAY',
    SUNDAY: 'SUNDAY',
} as const

export type PerformanceGoalCycleStartDay = keyof typeof PerformanceGoalCycleStartDay

export type PerformanceGoalCycleStartDate = number

export interface AccountPerformanceGoals {
    primaryGoal: PrimaryPerformanceGoal
    secondaryGoal: SecondaryPerformanceGoal
    goalAmount: number | null
    goalCycle: PerformanceGoalCycle
    cycleStartDay?: PerformanceGoalCycleStartDay | null
    cycleStartDate?: PerformanceGoalCycleStartDate | null
}

export interface CampaignGroup {
    id: UUID
    name: string
    campaignGroupType: CampaignGroupType
    performanceMode: PerformanceMode
    clicks: number // Retrieved live in getCampaignGroups
    cost: number // Retrieved live in getCampaignGroups
    conversions: number // Retrieved live in getCampaignGroups
    conversionsValue: number
    desiredCpa: number | null
    desiredRoas: number | null
    targetMode: TargetMode
    performanceTarget: number // Based on your account target and relevant metric
    campaigns: CampaignGroupCampaign[]
    conversionActions: AccountConversionAction[] // Default to the account level conversion actions, can be overrriden per campaign group
}

export interface CampaignGroupCampaign {
    campaignId: string
    campaignName: string
    campaignType: string // human readable campaign Type for the frontend
    campaignStrategy: string // human readable campaign Strategy for the frontend
    cost: number
    conversions: number
    conversionsValue: number
    clicks?: number
    campaignStatus: 'ENABLED' | 'PAUSED' | 'CANCELLED' | 'REMOVED' | 'ENDED' | 'UNKNOWN'
}

export interface EditableCampaignGroupFields {
    name?: string
    targetMode?: TargetMode
    conversionActions?: AccountConversionAction[]
    desiredCpa?: number
    desiredRoas?: number
}

export interface AccountConversionAction {
    conversionActionId: string
    conversionActionName: string
    conversionActionCategory: string
    isPrimary: boolean
    enabled: boolean
    conversions: number
    conversionsValue: number
}

export interface AccountConversionActionOnPlatform {
    conversionActionId: string
    conversionActionName: string
    conversionActionCategory: string
    isIncludedByDefault: boolean
    conversions: number
    conversionsValue: number
}

/**
 *  @deprecated use the above CampaignGroup instead
 */
export type CampaignGroupLegacy = {
    name: string
    campaign_ids: string[]
    conversion_types: string[]
    is_branding?: boolean
    conversion_types_raw?: ConversionType[]
    cost?: number
    clicks?: number
    conversions?: number
    auto_target?: boolean
    target?: number
    cpa?: number
    open?: boolean
    campaigns?: Campaign[]
    bidding_strategy?: BiddingStrategy
    target_roas?: number
}

export interface ConversionType {
    conversion_type: string
    enabled: boolean
    count: number
}

export interface Campaign {
    campaign_id: string
    campaign_label: string
    formatted_campaign_type: string // Only used in the frontend
    campaign_type: enums.AdvertisingChannelType
    campaign_subtype: enums.AdvertisingChannelSubType
    campaign_status: enums.CampaignStatus
    campaign_serving_status: enums.CampaignServingStatus
}

export interface CampaignMetrics {
    campaign: Pick<resources.Campaign, 'id' | 'resource_name'>
    metrics: Pick<
        common.Metrics,
        'all_conversions' | 'clicks' | 'all_conversions_value' | 'cost_micros'
    >
    segments: Pick<common.Segments, 'date'>
}

export interface CampaignGroupsMetrics {
    [campaign_group_id: string]: LookBackWindowMetrics[]
}

export interface LookBackWindowMetrics {
    lookback_window: number
    clicks: number
    cost: number
    conversions: number
    conversions_value: number
    conversion_rate: number
    cpa: number
    roas: number
    group_campaign_ids: string[]
}

export interface CampaignOptimisationTargets {
    [campaign_id: string]: CampaignsWithTargets
}

export interface CampaignsWithTargets {
    campaign_id: string
    campaign_group_name: string
    campaign_group_id: string
    conversion_action_names: string[]
    campaigns_in_group: string[]
    target_cpa?: number // a currency amount, like $50.41
    target_cpa_set: boolean // false if the user selected "auto" in campaign_groups UI
    target_roas_set: boolean // false if the user selected "auto" in campaign_groups UI
    campaign_name?: string
    bidding_strategy: BiddingStrategy
    target_roas?: number // a decimal number from 0 to ∞. eg 1.2 would be 120% return on ad spend (so 20% "profit"), 0.5 would be only getting half of your cost back as conversion_value.
}

export interface CampaignGroupOptimisationTargets {
    campaign_group_name: string
    campaign_group_id: UUID
    bidding_strategy: BiddingStrategy
    target_cpa: number | undefined
    target_roas: number | undefined
    target_cpa_set: boolean
    target_roas_set: boolean
    conversion_action_names: string[]
    campaign_ids: string[]
    campaign_rns: string[]
}

export enum OptimisationTarget {
    'TARGET_ROAS' = 'target_roas',
    'TARGET_CPA' = 'target_cpa',
}

export interface ThresholdContext {
    poor_cpa_performance_tolerance: number
    poor_roas_performance_tolerance: number
    performance_target_settings: {
        performance_mode: PerformanceMode
        target_cpa?: number
        target_roas?: number
        target_cpa_set: boolean
        target_roas_set: boolean
    }
    environment: {
        cost: number
        all_conversions_value: number
        all_conversions: number
    }
    entity: {
        cost: number
        all_conversions_value: number
        all_conversions: number
    }
}

export interface ThresholdReturn {
    performance_is_tolerable: boolean
    is_target_set: boolean
    environment: {
        cpa?: number
        roas?: number
        profit?: number
        roi?: number
    }
    entity: {
        cpa?: number
        roas?: number
        profit?: number
        roi?: number
    }
}

export interface RelevantCampaign {
    cpa: number
    campaign_id: string
    campaign: string
    cost: number
    clicks: number
    raw_conversions: {
        conversions: number
        conversion_name: string
    }[]
    conversion_group_cpa: number | undefined
    conversion_group_target: number | undefined
    conversion_types: string[]
    conversion_group_name: string
    conversion_group_cost: number | undefined
    conversion_group_conversions: number | undefined
    conversion_group_clicks: number | undefined
    conversions: number
}

export interface CampaignCost {
    campaign_id: string
    campaign: string
    cost: number
    clicks: number
}

export interface CampaignConversion {
    campaign_id: string
    conversions: number
    conversion_name: string
}

export interface CampaignInfo {
    campaign_id: string
    campaign: string
    advertising_channel: enums.AdvertisingChannelType
    advertising_sub_channel: enums.AdvertisingChannelSubType
    campaign_status: enums.CampaignStatus
    campaign_serving_status: enums.CampaignServingStatus
}
